/**
 * The directory. Everyone we have a record of, whether or not they can sign in.
 *
 * This is a top-level destination rather than an admin one, because looking a
 * colleague or a client contact up is ordinary work — every employee role holds
 * `person.view`, and neither portal role does (migration 0014). Creating and
 * editing still need `user.manage`, so most people who open this page get a
 * table and no buttons, which is the intended shape.
 *
 * Role, status, and last-seen live on the account (phase 2 §2.1) and a person
 * may have none. The list renders that as "No account" rather than as three
 * blanks: a contact with no login is an ordinary record here, not a row somebody
 * forgot to finish. Managing those accounts is Settings → Users.
 *
 * **Creating a person sends nothing.** §2.5 splits provisioning: this form
 * records a human, and giving them access is a separate act that sends the
 * invitation. There is no quiet path to an account, because an account nobody
 * was told about is the failure mode §2.4 exists to prevent.
 */

import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useState } from 'react';
import { Form } from 'react-aria-components';
import { useCreatePerson, useMe, usePeople } from '../../api/queries.ts';
import {
  type CreatePersonResponse,
  USER_ROLES,
  USER_STATUSES,
  type UserRole,
  type UserStatus,
} from '../../api/types.ts';
import { roleCarries } from '../../components/capabilities.ts';
import { formatRelative } from '../../components/format.ts';
import { Badge, RoleBadge, roleLabel, StatusBadge } from '../../components/ui/badge.tsx';
import { Button } from '../../components/ui/button.tsx';
import { Dialog, DialogBody, DialogFooter } from '../../components/ui/dialog.tsx';
import { Alert, ErrorState } from '../../components/ui/feedback.tsx';
import { Select, TextField } from '../../components/ui/field.tsx';
import { Card, PageHeader } from '../../components/ui/layout.tsx';
import { Table, TableEmpty, TBody, TD, TH, THead, TR } from '../../components/ui/table.tsx';

const STATUS_OPTIONS = [
  { value: 'all' as const, label: 'Any status' },
  ...USER_STATUSES.map((status) => ({ value: status, label: status })),
  // Not a status: the absence of the row a status would live on (§2.1).
  { value: 'none' as const, label: 'No account' },
];

const ROLE_FILTER_OPTIONS = [
  { value: 'all' as const, label: 'Any role' },
  ...USER_ROLES.map((role) => ({ value: role, label: roleLabel(role) })),
];

function CreatePersonDialog({ onClose }: { onClose: () => void }) {
  const create = useCreatePerson();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [result, setResult] = useState<CreatePersonResponse | null>(null);

  if (result) {
    return (
      <>
        <DialogBody>
          <Alert tone="success" title="Recorded, and nothing was sent">
            <p>
              {result.person.name} exists as a person and cannot sign in — there is no account for a
              session to reference. Open their record and give them access when they need it, which
              is the step that sends the invitation.
            </p>
          </Alert>
        </DialogBody>
        <DialogFooter>
          <Link
            to="/people/$personId"
            params={{ personId: result.person.id }}
            className="no-underline"
          >
            <Button variant="secondary">Open their record</Button>
          </Link>
          <Button variant="primary" onPress={onClose}>
            Done
          </Button>
        </DialogFooter>
      </>
    );
  }

  return (
    <Form
      onSubmit={(event) => {
        event.preventDefault();
        create.mutate({ name: name.trim(), email: email.trim() }, { onSuccess: setResult });
      }}
    >
      <DialogBody>
        <TextField label="Full name" value={name} onChange={setName} isRequired autoFocus />
        <TextField
          label="Email address"
          type="email"
          value={email}
          onChange={setEmail}
          isRequired
          description="Admin-changeable later. SSO matches on it, so a name change at a client must not lock anyone out."
        />
        {create.isError ? <ErrorState error={create.error} /> : null}
      </DialogBody>
      <DialogFooter>
        <Button variant="tertiary" onPress={onClose}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          isPending={create.isPending}
          isDisabled={name.trim() === '' || email.trim() === ''}
        >
          Create person
        </Button>
      </DialogFooter>
    </Form>
  );
}

export function PeopleListPage() {
  const search = useSearch({ from: '/_authed/_employee/people/' });
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);

  const filters = {
    // "No account" is a filter on whether the account row exists, not on its
    // status, so it travels as its own parameter (§2.1).
    ...(search.status === 'none' ? { account: 'none' as const } : {}),
    ...(search.status && search.status !== 'none' ? { status: search.status } : {}),
    ...(search.role ? { role: search.role } : {}),
    ...(search.q ? { q: search.q } : {}),
  };
  const people = usePeople(filters);
  const me = useMe();
  const canManage = me.data ? roleCarries(me.data.user.role, 'user.manage') : false;

  const setFilter = (patch: Record<string, string | undefined>) => {
    void navigate({ to: '/people', search: { ...search, ...patch }, replace: true });
  };

  return (
    <>
      <PageHeader
        title="People"
        description="Everyone we have a record of, whichever side they are on, including contacts who have no login at all. Whether somebody can sign in is a separate record — Settings → Users is where those are managed."
        actions={
          canManage ? (
            <Button variant="primary" onPress={() => setCreating(true)}>
              Create person
            </Button>
          ) : null
        }
      />

      <Card padded={false}>
        <div className="flex flex-col gap-3 border-b border-secondary p-4 sm:flex-row sm:items-end">
          <TextField
            label="Search"
            type="search"
            className="sm:max-w-xs"
            value={search.q ?? ''}
            onChange={(value) => setFilter({ q: value || undefined })}
            placeholder="Name or email"
          />
          <Select
            label="Status"
            className="sm:w-44"
            options={STATUS_OPTIONS}
            selectedKey={search.status ?? 'all'}
            onSelectionChange={(value) =>
              setFilter({ status: value === 'all' ? undefined : (value as UserStatus | 'none') })
            }
          />
          <Select
            label="Role"
            className="sm:w-48"
            options={ROLE_FILTER_OPTIONS}
            selectedKey={search.role ?? 'all'}
            onSelectionChange={(value) =>
              setFilter({ role: value === 'all' ? undefined : (value as UserRole) })
            }
          />
        </div>

        {people.isError ? (
          <div className="p-4">
            <ErrorState error={people.error} />
          </div>
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>Name</TH>
                <TH>Role</TH>
                <TH>Account</TH>
                <TH>Sign-in</TH>
                <TH>Last seen</TH>
                <TH className="text-right">Access</TH>
              </TR>
            </THead>
            <TBody>
              {people.isPending ? (
                <TableEmpty colSpan={6}>Loading…</TableEmpty>
              ) : people.data.people.length === 0 ? (
                <TableEmpty colSpan={6}>Nobody matches those filters.</TableEmpty>
              ) : (
                people.data.people.map((person) => (
                  <TR key={person.id} className="hover:bg-secondary">
                    <TD>
                      <Link
                        to="/people/$personId"
                        params={{ personId: person.id }}
                        className="font-medium text-primary no-underline hover:underline"
                      >
                        {person.name}
                      </Link>
                      <span className="block text-xs text-tertiary">{person.email}</span>
                    </TD>
                    <TD>
                      {person.account ? (
                        <RoleBadge role={person.account.role} />
                      ) : (
                        <span className="text-sm text-tertiary">—</span>
                      )}
                    </TD>
                    <TD>
                      {person.account ? (
                        <StatusBadge status={person.account.status} />
                      ) : (
                        <Badge tone="outline">No account</Badge>
                      )}
                    </TD>
                    <TD>
                      {person.account ? (
                        <Badge tone="outline" mono>
                          {person.account.authMethod}
                        </Badge>
                      ) : (
                        <span className="text-sm text-tertiary">—</span>
                      )}
                    </TD>
                    <TD className="whitespace-nowrap">
                      {person.account ? formatRelative(person.account.lastSeenAt) : '—'}
                    </TD>
                    <TD className="text-right">
                      <Link
                        to="/schema/access/people/$personId"
                        params={{ personId: person.id }}
                        className="text-sm font-medium link"
                      >
                        What they reach
                      </Link>
                    </TD>
                  </TR>
                ))
              )}
            </TBody>
          </Table>
        )}
      </Card>

      <Dialog
        title="Create a person"
        description="A record of a human. Giving them a login is a separate step on their page, and that step is what sends the invitation."
        isOpen={creating}
        onOpenChange={setCreating}
      >
        {(close) => <CreatePersonDialog onClose={close} />}
      </Dialog>
    </>
  );
}
